// src/pages/AccessDeniedPage.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePermissions } from "../context/PermissionContext";
import { toast } from "react-toastify";

const AccessDeniedPage = () => {
  
   const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);
  const { refreshAuthStatus, isAuthenticated } = usePermissions();
  const navigate = useNavigate();
  if (!isAuthenticated) {
    return navigate("/sign-in");
  }
  const logout = async () => {
    localStorage.clear();
    await refreshAuthStatus();
    toast.success("Logout Successful");
    navigate("/sign-in", { replace: true });
  };

  return (
      <>
      <div className="d-flex align-items-center justify-content-center vh-100 position-relative overflow-hidden bg-dark">
        {/* Animated background elements */}
        <div className="position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.3 }}>
          <div 
            className="position-absolute rounded-circle" 
            style={{
              top: '10%',
              left: '10%',
              width: '400px',
              height: '400px',
              background: 'rgba(220, 38, 38, 0.1)',
              filter: 'blur(80px)',
              animation: 'pulse 3s ease-in-out infinite'
            }}
          ></div>
          <div 
            className="position-absolute rounded-circle" 
            style={{
              bottom: '10%',
              right: '10%',
              width: '400px',
              height: '400px',
              background: 'rgba(245, 158, 11, 0.1)',
              filter: 'blur(80px)',
              animation: 'pulse 3s ease-in-out infinite 1s'
            }}
          ></div>
        </div>
        
        {/* Grid pattern overlay */}
        <div 
          className="position-absolute top-0 start-0 w-100 h-100" 
          style={{
            opacity: 0.02,
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        ></div>

        <div 
          className={`position-relative text-center px-4 transition-all ${mounted ? 'fade-in-up' : 'opacity-0'}`}
          style={{ maxWidth: '700px', zIndex: 10 }}
        >
          {/* Lock icon decoration */}
          <div className="mb-4 d-inline-block">
            <div className="position-relative">
              <div 
                className="d-flex align-items-center justify-content-center mx-auto icon-container"
                style={{
                  width: '100px',
                  height: '100px',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  borderRadius: '1rem',
                  boxShadow: '0 20px 40px rgba(220, 38, 38, 0.5)',
                  transition: 'transform 0.3s ease'
                }}
              >
                <svg 
                  className="text-white" 
                  width="48" 
                  height="48" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div 
                className="position-absolute rounded" 
                style={{
                  top: '-8px',
                  left: '-8px',
                  right: '-8px',
                  bottom: '-8px',
                  background: 'linear-gradient(135deg, #dc2626, #f59e0b)',
                  filter: 'blur(20px)',
                  opacity: 0.3,
                  animation: 'pulse 2s ease-in-out infinite',
                  zIndex: -1
                }}
              ></div>
            </div>
          </div>

          {/* Error code with gradient */}
          <h1 
            className="mb-3 fw-bold error-code"
            style={{
              fontSize: 'clamp(5rem, 15vw, 10rem)',
              background: 'linear-gradient(135deg, #ef4444 0%, #f59e0b 50%, #ef4444 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em',
              lineHeight: '1',
              animation: 'gradient-shift 3s ease infinite'
            }}
          >
            404
          </h1>

          {/* Title */}
          <h2 className="display-4 fw-bold text-white mb-4">
            Access <span className="gradient-text">Denied</span>
          </h2>

          {/* Description */}
          <p className="lead text-white-50 mb-5 mx-auto" style={{ maxWidth: '500px' }}>
            You don't have permission to access this resource. The page may be restricted or no longer available.
          </p>

          {/* Action buttons */}
          <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center mb-5">
            <Link 
              to="/" 
              className="btn btn-lg px-5 py-3 fw-semibold rounded-3 btn-primary-custom d-flex align-items-center justify-content-center gap-2"
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>

            <button
              onClick={logout}
              className="btn btn-lg px-5 py-3 fw-semibold rounded-3 btn-outline-custom d-flex align-items-center justify-content-center gap-2"
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>

          {/* Decorative element */}
          <div className="d-flex align-items-center justify-content-center gap-3 text-white-50 small">
            <div style={{ height: '1px', width: '50px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.2))' }}></div>
            <span>Error Code: 404 Forbidden</span>
            <div style={{ height: '1px', width: '50px', background: 'linear-gradient(to left, transparent, rgba(255,255,255,0.2))' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes gradient-shift {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.05);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .fade-in-up {
          animation: fadeInUp 1s ease forwards;
        }

        .opacity-0 {
          opacity: 0;
        }

        .transition-all {
          transition: all 0.3s ease;
        }

        .gradient-text {
          background: linear-gradient(135deg, #ef4444, #f59e0b);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .icon-container:hover {
          transform: scale(1.1);
        }

        .btn-primary-custom {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          border: none;
          color: white;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .btn-primary-custom:hover {
          transform: scale(1.05);
          box-shadow: 0 15px 35px rgba(220, 38, 38, 0.5);
        }

        .btn-primary-custom svg {
          transition: transform 0.3s ease;
        }

        .btn-primary-custom:hover svg {
          transform: translateX(-4px);
        }

        .btn-primary-custom::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #b91c1c, #991b1b);
          transition: left 0.3s ease;
          z-index: -1;
        }

        .btn-primary-custom:hover::before {
          left: 0;
        }

        .btn-outline-custom {
          background: rgba(30, 41, 59, 0.5);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(220, 38, 38, 0.3);
          color: white;
          transition: all 0.3s ease;
        }

        .btn-outline-custom:hover {
          border-color: #dc2626;
          background: rgba(30, 41, 59, 0.8);
          box-shadow: 0 10px 25px rgba(220, 38, 38, 0.2);
          color: white;
        }

        .btn-outline-custom svg {
          transition: transform 0.5s ease;
        }

        .btn-outline-custom:hover svg {
          transform: rotate(180deg);
        }

        .bg-dark {
          background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%) !important;
        }
      `}</style>
    </>
  );
};

export default AccessDeniedPage;
